import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useMediaQuery } from '@mui/material';

import { ProducerNavModule } from '../producer-nav-config';

const ICON_RAIL_WIDTH = 64;
const SEC_NAV_WIDTH = 226;
const HOVER_DEBOUNCE_MS = 150;
const MOBILE_TOP_BAR_HEIGHT = 56;

function getStoredBoolean(key: string, fallback: boolean): boolean {
  if (typeof window === 'undefined') return fallback;
  const stored = localStorage.getItem(key);
  if (stored === null) return fallback;
  return stored === 'true';
}

export function useProducerNav(modules: ProducerNavModule[]) {
  const { pathname } = useLocation();
  const isMobile = useMediaQuery('(max-width: 899px)');

  // Persisted state
  const [isPinned, setIsPinned] = useState(() =>
    getStoredBoolean('producerNavPinned', false),
  );

  // Transient state
  const [hoveredModule, setHoveredModuleState] = useState<string | null>(null);
  const [isMobileDrawerOpen, setIsMobileDrawerOpen] = useState(false);

  // Hover debounce
  const hoverTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearHoverTimer = useCallback(() => {
    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = null;
    }
  }, []);

  const setHoveredModule = useCallback(
    (moduleId: string | null) => {
      clearHoverTimer();
      if (moduleId) {
        setHoveredModuleState(moduleId);
      } else {
        hoverTimerRef.current = setTimeout(() => {
          setHoveredModuleState(null);
        }, HOVER_DEBOUNCE_MS);
      }
    },
    [clearHoverTimer],
  );

  // Cleanup timer on unmount
  useEffect(() => clearHoverTimer, [clearHoverTimer]);

  // Active module detection
  const activeModule = useMemo(() => {
    for (const mod of modules) {
      if (mod.href && pathname.startsWith(mod.href)) return mod.id;
      if (mod.sections) {
        for (const section of mod.sections) {
          for (const item of section.items) {
            if (pathname.startsWith(item.href)) return mod.id;
          }
        }
      }
    }
    return null;
  }, [pathname, modules]);

  // The module to show in secnav: hovered takes priority, then active if pinned
  const visibleModule = hoveredModule ?? (isPinned ? activeModule : null);
  // Only show sec nav if the visible module actually has sections
  const visibleModuleData = visibleModule
    ? modules.find((m) => m.id === visibleModule)
    : undefined;
  const isSecNavVisible =
    visibleModule !== null &&
    !!visibleModuleData?.sections &&
    visibleModuleData.sections.length > 0;

  // Toggle pin
  const togglePin = useCallback(() => {
    setIsPinned((prev) => {
      const next = !prev;
      localStorage.setItem('producerNavPinned', String(next));
      return next;
    });
  }, []);

  // Mobile drawer
  const toggleMobileDrawer = useCallback(() => {
    setIsMobileDrawerOpen((prev) => !prev);
  }, []);

  const closeMobileDrawer = useCallback(() => {
    setIsMobileDrawerOpen(false);
  }, []);

  const contentMarginTop = useMemo(
    () => (isMobile ? MOBILE_TOP_BAR_HEIGHT : 0),
    [isMobile],
  );

  return {
    isMobile,
    isPinned,
    togglePin,
    hoveredModule,
    setHoveredModule,
    activeModule,
    visibleModule,
    isSecNavVisible,
    isMobileDrawerOpen,
    toggleMobileDrawer,
    closeMobileDrawer,

    contentMarginTop,
    ICON_RAIL_WIDTH,
    SEC_NAV_WIDTH,
    MOBILE_TOP_BAR_HEIGHT,
  };
}
